import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';

const { Option } = Select;

interface InsumoModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  insumo?: {
    id: number;
    codbarra: string;
    descripcion: string;
    stockminimo: number;
    ubicacion: string;
    unidad: string;
    tasaiva: number;
    id_grupo: number;
    id_subgrupo: number;
    id_marca: number;
  } | null;
}

interface GrupoData {
  id: number;
  descripcion: string;
  clasificacion: number;
}

const InsumoModal: React.FC<InsumoModalProps> = ({ visible, onClose, onSuccess, insumo }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const empresa = user?.id_empresa;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [marcas, setMarcas] = useState<GrupoData[]>([]);
  const [grupos, setGrupos] = useState<GrupoData[]>([]);
  const [subgrupos, setSubgrupos] = useState<GrupoData[]>([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);

  const isEditing = !!insumo;

  // Cargar catálogos (Marcas, Grupos, Subgrupos)
  const fetchCatalogos = async () => {
    setLoadingCatalogos(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_objetos/${empresa}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar catálogos');

      const data = await response.json();
      setMarcas(data.filter((item: GrupoData) => item.clasificacion === 3));
      setGrupos(data.filter((item: GrupoData) => item.clasificacion === 1));
      setSubgrupos(data.filter((item: GrupoData) => item.clasificacion === 2));
    } catch (error) {
      message.error('Error al cargar los catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  // Cargar datos si es edición
  useEffect(() => {
    if (visible) {
      fetchCatalogos();
      if (insumo) {
        form.setFieldsValue({
          codbarra: insumo.codbarra,
          descripcion: insumo.descripcion,
          stockminimo: insumo.stockminimo,
          ubicacion: insumo.ubicacion,
          unidad: insumo.unidad,
          tasaiva: insumo.tasaiva,
          id_grupo: insumo.id_grupo,
          id_subgrupo: insumo.id_subgrupo,
          id_marca: insumo.id_marca
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, insumo, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const accessToken = await getAccessTokenSilently();
      const url = isEditing
        ? `${process.env.REACT_APP_INSUMOS_URL}/editar_insumos/${empresa}/${insumo!.id}`
        : `${process.env.REACT_APP_INSUMOS_URL}/crear_insumos`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...values,
          ...(isEditing ? {} : { id_empresa: empresa })
        }),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      message.success(`Insumo ${isEditing ? 'actualizado' : 'creado'} correctamente`);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el insumo`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: theme.colors.primaryDark, 
          fontSize: theme.fontSizes.large,
          fontWeight: 600
        }}>
          {isEditing ? 'Editar Insumo' : 'Nuevo Insumo'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
      width={700}
      okButtonProps={{
        style: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          fontWeight: 500
        }
      }}
      cancelButtonProps={{
        style: {
          fontWeight: 500
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item 
            name="codbarra" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Código de Barra</span>}
            rules={[{ required: true, message: 'Por favor ingrese el código de barra' }]}
          >
            <Input maxLength={19} placeholder="Código de barra" />
          </Form.Item>

          <Form.Item 
            name="descripcion" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>}
            rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
          >
            <Input maxLength={40} placeholder="Descripción del insumo" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item 
            name="stockminimo" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Stock Mínimo</span>}
          >
            <Input type="number" placeholder="Stock mínimo" />
          </Form.Item>

          <Form.Item 
            name="ubicacion" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Ubicación Depósito</span>}
          >
            <Input maxLength={7} placeholder="Ej: A-01-B" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item 
            name="unidad" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Unidad de Medida</span>}
            rules={[{ required: true, message: 'Por favor ingrese la unidad' }]}
          >
            <Input maxLength={5} placeholder="Ej: KG, LT, UN" />
          </Form.Item>

          <Form.Item 
            name="tasaiva" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Tasa de IVA</span>}
            rules={[{ required: true, message: 'Por favor seleccione la tasa de IVA' }]}
          >
            <Select placeholder="Seleccione tasa de IVA">
              <Option value={21}>21%</Option>
              <Option value={10.5}>10.5%</Option>
              <Option value={27}>27%</Option>
              <Option value={0}>0%</Option>
            </Select>
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <Form.Item 
            name="id_grupo" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Grupo</span>}
            rules={[{ required: true, message: 'Por favor seleccione un grupo' }]}
          >
            <Select
              placeholder="Seleccione un grupo"
              loading={loadingCatalogos}
              showSearch
              optionFilterProp="children"
            >
              {grupos.map(grupo => (
                <Option key={grupo.id} value={grupo.id}>
                  {grupo.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="id_subgrupo" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Subgrupo</span>}
            rules={[{ required: true, message: 'Por favor seleccione un subgrupo' }]}
          >
            <Select
              placeholder="Seleccione un subgrupo"
              loading={loadingCatalogos}
              showSearch
              optionFilterProp="children"
            >
              {subgrupos.map(subgrupo => (
                <Option key={subgrupo.id} value={subgrupo.id}>
                  {subgrupo.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="id_marca" 
            label={<span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Marca</span>}
            rules={[{ required: true, message: 'Por favor seleccione una marca' }]}
          >
            <Select
              placeholder="Seleccione una marca"
              loading={loadingCatalogos}
              showSearch
              optionFilterProp="children"
            >
              {marcas.map(marca => (
                <Option key={marca.id} value={marca.id}>
                  {marca.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default InsumoModal;

